import React, { useEffect, useState } from 'react';

import axios from 'axios';

import './ApiLogger.scss';

// Check debug mode ONLY in sessionStorage (set by SongGenerator with ?debug=true)
// This ensures the logger is controlled exclusively via URL parameter
const isLoggerEnabled = () => {
  return sessionStorage.getItem('songGeneratorDebug') === 'true';
};

interface ApiLogEntry {
  id: string;
  type: 'request' | 'response' | 'error';
  method: string;
  url: string;
  status?: number;
  data?: any;
  timestamp: Date;
  count: number; // Количество повторений
}

// Ключ для группировки запросов
const getLogKey = (log: Omit<ApiLogEntry, 'id' | 'timestamp' | 'count'>): string => {
  return `${log.type}:${log.method}:${log.url}:${log.status || 'pending'}`;
};

export const ApiLogger: React.FC = () => {
  const [logs, setLogs] = useState<ApiLogEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [enabled, setEnabled] = useState(isLoggerEnabled);

  // Check debug mode periodically (in case it's enabled via URL param)
  useEffect(() => {
    const checkInterval = setInterval(() => {
      setEnabled(isLoggerEnabled());
    }, 500);
    return () => clearInterval(checkInterval);
  }, []);

  useEffect(() => {
    // We only attach interceptors if the logger is enabled
    if (!enabled) return;

    // Use axios.defaults directly since api.ts uses it
    const reqInterceptor = axios.interceptors.request.use((config) => {
      const newLog: Omit<ApiLogEntry, 'id' | 'timestamp' | 'count'> = {
        type: 'request',
        method: config.method?.toUpperCase() || 'UNKNOWN',
        url: config.url || '',
        data: config.data || config.params,
      };
      
      const key = getLogKey(newLog);
      const now = new Date();
      
      setLogs(prev => {
        // Ищем последний такой же запрос
        const lastLog = prev[prev.length - 1];
        if (lastLog && getLogKey(lastLog) === key) {
          // Увеличиваем счётчик
          return prev.map((log, idx) => 
            idx === prev.length - 1 
              ? { ...log, count: log.count + 1, timestamp: now }
              : log
          );
        }
        
        // Добавляем новый запрос
        const log: ApiLogEntry = {
          id: Math.random().toString(36).substring(7),
          ...newLog,
          timestamp: now,
          count: 1
        };
        return [...prev].slice(-100).concat(log);
      });
      
      return config;
    });

    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        const newLog: Omit<ApiLogEntry, 'id' | 'timestamp' | 'count'> = {
          type: 'response',
          method: response.config.method?.toUpperCase() || 'UNKNOWN',
          url: response.config.url || '',
          status: response.status,
          data: response.data,
        };
        
        const key = getLogKey(newLog);
        const now = new Date();
        
        setLogs(prev => {
          const lastLog = prev[prev.length - 1];
          if (lastLog && getLogKey(lastLog) === key) {
            return prev.map((log, idx) => 
              idx === prev.length - 1 
                ? { ...log, count: log.count + 1, timestamp: now }
                : log
            );
          }
          
          const log: ApiLogEntry = {
            id: Math.random().toString(36).substring(7),
            ...newLog,
            timestamp: now,
            count: 1
          };
          return [...prev].slice(-100).concat(log);
        });
        
        return response;
      },
      (error) => {
        const newLog: Omit<ApiLogEntry, 'id' | 'timestamp' | 'count'> = {
          type: 'error',
          method: error.config?.method?.toUpperCase() || 'UNKNOWN',
          url: error.config?.url || '',
          status: error.response?.status,
          data: error.response?.data || error.message,
        };
        
        const key = getLogKey(newLog);
        const now = new Date();
        
        setLogs(prev => {
          const lastLog = prev[prev.length - 1];
          if (lastLog && getLogKey(lastLog) === key) {
            return prev.map((log, idx) => 
              idx === prev.length - 1 
                ? { ...log, count: log.count + 1, timestamp: now }
                : log
            );
          }
          
          const log: ApiLogEntry = {
            id: Math.random().toString(36).substring(7),
            ...newLog,
            timestamp: now,
            count: 1
          };
          return [...prev].slice(-100).concat(log);
        });
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [enabled]);

  if (!enabled) return null;

  const totalCount = logs.reduce((sum, log) => sum + log.count, 0);

  if (!isOpen) {
    return (
      <div className="api-logger-toggle" onClick={() => setIsOpen(true)}>
        📡 API {totalCount > 0 && `(${totalCount})`}
      </div>
    );
  }

  return (
    <div className="api-logger-window">
      <div className="api-logger-header">
        <div className="api-logger-title">📡 Network Logs</div>
        <div className="api-logger-actions">
           <button onClick={() => setLogs([])}>Clear</button>
           <button onClick={() => setIsOpen(false)}>×</button>
        </div>
      </div>
      <div className="api-logger-content">
        {logs.length === 0 ? (
          <div className="api-logger-empty">Waiting for requests...</div>
        ) : (
          logs.map(log => (
            <div key={log.id} className={`api-log-entry ${log.type}`}>
              <div className="api-log-info">
                <span className="api-log-time">{log.timestamp.toLocaleTimeString()}</span>
                <span className={`api-log-method ${log.method.toLowerCase()}`}>{log.method}</span>
                <span className="api-log-url">{log.url.split('?')[0]}</span>
                {log.status && (
                  <span className={`api-log-status ${log.status >= 400 ? 'status-error' : 'status-ok'}`}>
                    {log.status}
                  </span>
                )}
                {log.count > 1 && (
                  <span className="api-log-count" title={`Повторений: ${log.count}`}>
                    ×{log.count}
                  </span>
                )}
              </div>
              {log.data && (
                <pre className="api-log-data">
                  {JSON.stringify(log.data, null, 2)}
                </pre>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
